// Bottom-left in-match input hint: "{key} to pause".
//
// Mirrors the small "skip tutorial" banner from TutorialHintBanner — same
// keycap visual language and the same press-to-depress feel — but
// positioned bottom-left and shown during local + online matches. Hidden
// on touch input modality: mobiles already have a visible ⋯ menu button,
// and the bottom-left corner overlaps the joystick on small screens.
//
// Lifecycle: construct after PauseController.start(); call tick() per
// render frame; dispose() at match teardown.

import { t, onLocaleChange } from '../i18n';
import type { Input, InputModality } from '../core/Input';
import {
  ensureControlHintStyles,
  gamepadGlyph,
  isControlHeld,
  labelFor,
  sampleInputState
} from './controlHintShared';

export class PauseHintBanner {
  private input: Input;
  private root: HTMLElement;
  private card: HTMLElement;
  private modality: InputModality;
  private detachLocale: (() => void) | null = null;

  // Re-render the keycap when modality changes (kbm "P" → gamepad Start
  // glyph). null forces an initial render on first tick().
  private lastModality: InputModality | null = null;

  // The single keycap element we update each frame for held-state.
  private keyEl: HTMLElement | null = null;

  // Per-frame keyboard / mouse state — banner owns its own listeners so it
  // can read held state without piping anything new through Input.
  private keys = new Set<string>();
  private mouseL = false;
  private mouseR = false;

  private onKeyDown = (e: KeyboardEvent): void => { this.keys.add(e.code); };
  private onKeyUp = (e: KeyboardEvent): void => { this.keys.delete(e.code); };
  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseL = true;
    if (e.button === 2) this.mouseR = true;
  };
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseL = false;
    if (e.button === 2) this.mouseR = false;
  };

  constructor(input: Input) {
    this.input = input;
    this.modality = input.getModality();

    ensureControlHintStyles();

    this.root = document.createElement('div');
    this.root.id = 'match-hint-pause';
    this.root.className = 'thb thb-pause-pos';
    this.card = document.createElement('div');
    this.card.className = 'thb-card thb-card-small';
    this.card.style.opacity = '0';
    this.root.appendChild(this.card);
    document.body.appendChild(this.root);

    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('mousedown', this.onMouseDown);
    addEventListener('mouseup', this.onMouseUp);

    this.detachLocale = onLocaleChange(() => {
      // Force re-render on locale change.
      this.lastModality = null;
    });
  }

  dispose(): void {
    removeEventListener('keydown', this.onKeyDown);
    removeEventListener('keyup', this.onKeyUp);
    removeEventListener('mousedown', this.onMouseDown);
    removeEventListener('mouseup', this.onMouseUp);
    this.detachLocale?.();
    this.detachLocale = null;
    this.root.remove();
  }

  /** Called once per render frame. */
  tick(): void {
    this.modality = this.input.getModality();

    // Mobile / touch: hide entirely. The on-screen ⋯ menu button is the
    // mobile pause affordance; a hint here would be redundant and clip
    // the joystick at the bottom-left of the screen.
    if (this.modality === 'touch') {
      if (this.root.style.display !== 'none') this.root.style.display = 'none';
      return;
    }

    if (this.root.style.display === 'none') this.root.style.display = '';

    if (this.modality !== this.lastModality) {
      this.render();
      this.lastModality = this.modality;
      // Trigger CSS transition by pushing opacity to 1 next frame.
      requestAnimationFrame(() => { this.card.style.opacity = '1'; });
    }

    this.refreshHeld();
  }

  private render(): void {
    const row = document.createElement('div');
    row.className = 'thb-row';

    const template = t('match.hint.pause');
    const parts = template.split(/(\{[a-zA-Z]+\})/g);
    const body = document.createElement('span');
    body.className = 'thb-row-body';

    this.keyEl = null;
    for (const part of parts) {
      const m = /^\{([a-zA-Z]+)\}$/.exec(part);
      if (m && m[1] === 'key') {
        body.appendChild(this.renderMenuKeycap());
      } else if (part.length > 0) {
        body.appendChild(document.createTextNode(part));
      }
    }

    row.appendChild(body);
    this.card.replaceChildren(row);
  }

  private renderMenuKeycap(): HTMLElement {
    const cap = document.createElement('span');
    cap.className = 'thb-key';
    cap.dataset.control = 'menu';

    if (this.modality === 'gamepad') {
      cap.classList.add('thb-key-gp');
      cap.innerHTML = gamepadGlyph('menu');
    } else {
      cap.textContent = labelFor('menu', this.modality);
    }

    this.keyEl = cap;
    return cap;
  }

  private refreshHeld(): void {
    if (!this.keyEl) return;
    const sample = sampleInputState(this.modality, this.keys, this.mouseL, this.mouseR);
    const held = isControlHeld('menu', this.modality, sample);
    this.keyEl.classList.toggle('held', held);
  }
}
